@let item = comparison();
<section class="order-comparison" [class.order-comparison-deviating]="deviationCount() > 0">
    <header class="order-comparison-header">
        <strong>{{ 'Abgleich mit der Bestellung' | translate }}</strong>
        @if (item.orderItemPosition !== null) {
            <span class="text-description ms-2">
                {{ 'Bestellposition' | translate }} {{ item.orderItemPosition }}
            </span>
        }
        <span class="order-comparison-status">
            @if (!item.linkedToOrder) {
                <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">
                    {{ 'Ohne Bestellbezug' | translate }}
                </span>
            } @else if (deviationCount() === 0) {
                <span class="badge rounded-pill bg-success-subtle text-success-emphasis">
                    <em class="fas fa-check me-1" aria-hidden="true"></em>
                    {{ 'Stimmt mit der Bestellung überein' | translate }}
                </span>
            } @else if (deviationCount() === 1) {
                <span class="badge rounded-pill bg-danger-subtle text-danger-emphasis">
                    <em class="fas fa-exclamation-triangle me-1" aria-hidden="true"></em>
                    {{ '\{\{count\}\} Abweichung' | translate: { count: 1 } }}
                </span>
            } @else {
                <span class="badge rounded-pill bg-danger-subtle text-danger-emphasis">
                    <em class="fas fa-exclamation-triangle me-1" aria-hidden="true"></em>
                    {{ '\{\{count\}\} Abweichungen' | translate: { count: deviationCount() } }}
                </span>
            }
        </span>
    </header>

    @if (!item.linkedToOrder) {
        <p class="order-comparison-note text-description">
            {{ 'Diese Position ist keiner Bestellposition zugeordnet und wird nicht verglichen.' | translate }}
        </p>
    }

    @if (showsTable()) {
        <table class="order-comparison-table">
            <colgroup>
                <col />
                <col />
                <col />
                <col />
            </colgroup>
            <thead>
                <tr>
                    <th scope="col">{{ 'Feld' | translate }}</th>
                    <th scope="col">{{ 'Rechnung' | translate }}</th>
                    <th scope="col">{{ 'Bestellung' | translate }}</th>
                    <th scope="col">{{ 'Unterschied' | translate }}</th>
                </tr>
            </thead>
            <tbody>
                @if (showsPriceRow()) {
                    <tr [class.order-comparison-deviation]="item.priceDeviates">
                        <th scope="row">
                            @if (item.priceDeviates) {
                                <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            }
                            @if (item.priceQuantity?.isDifferent) {
                                {{ 'Preis und Preismenge' | translate }}
                            } @else {
                                {{ 'Preis' | translate }}
                            }
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">
                            @if (item.price?.invoiceValue; as invoicePrice) {
                                <display-currency
                                    [value]="invoicePrice.amount"
                                    [currencyCode]="invoicePrice.currency"
                                />
                                {{ 'je' | translate }} {{ item.invoicePriceQuantity | number: '1.0-3' }}
                                {{ invoiceUnit() }}
                                @if (!item.bothPriceQuantitiesAreOne) {
                                    <div class="order-comparison-conversion">
                                        {{ 'entspricht' | translate }}
                                        <display-currency
                                            [value]="item.invoiceUnitPrice"
                                            [currencyCode]="invoicePrice.currency"
                                            [digitsInfo]="unitPriceDigitsInfo()"
                                        />
                                        {{ 'je' | translate }} {{ invoiceUnit() }}
                                    </div>
                                }
                            }
                        </td>
                        <td [attr.data-label]="'Bestellung' | translate">
                            @if (item.price?.orderValue; as orderPrice) {
                                <display-currency [value]="orderPrice.amount" [currencyCode]="orderPrice.currency" />
                                {{ 'je' | translate }} {{ item.orderPriceQuantity | number: '1.0-3' }}
                                {{ orderUnit() }}
                            }
                        </td>
                        <td [attr.data-label]="'Unterschied' | translate" class="fw-bold">
                            @if (unitPriceDifference(); as difference) {
                                @if (!item.bothPriceQuantitiesAreOne) {
                                    {{ 'je' | translate }} {{ invoiceUnit() }}
                                }
                                <span class="text-nowrap"
                                    >{{ difference.isPositive ? '+' : ''
                                    }}<display-currency
                                        [value]="difference.amount"
                                        [currencyCode]="difference.currency"
                                        [digitsInfo]="difference.digitsInfo"
                                /></span>
                                @if (difference.percent !== null) {
                                    <span class="order-comparison-percent">
                                        ({{ difference.isPositive ? '+' : ''
                                        }}{{ difference.percent | number: '1.1-1' }} %)
                                    </span>
                                }
                            } @else if (onlyPriceQuantityDiffers()) {
                                {{ 'je' | translate }} {{ invoiceUnit() }}
                                {{ 'gleich, nur die Preismenge ist anders' | translate }}
                            } @else {
                                —
                            }
                        </td>
                    </tr>
                }
                @if (item.currencyDeviates) {
                    <tr class="order-comparison-deviation">
                        <th scope="row">
                            <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            {{ 'Währung' | translate }}
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">{{ item.currency?.invoiceValue ?? '—' }}</td>
                        <td [attr.data-label]="'Bestellung' | translate">{{ item.currency?.orderValue ?? '—' }}</td>
                        <td [attr.data-label]="'Unterschied' | translate" class="fw-bold">
                            {{ 'andere Währung' | translate }}
                        </td>
                    </tr>
                }
                @if (item.taxDeviates) {
                    <tr class="order-comparison-deviation">
                        <th scope="row">
                            <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            {{ 'Steuersatz' | translate }}
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">
                            @if (item.taxInPercent?.invoiceValue !== null) {
                                {{ item.taxInPercent?.invoiceValue | number: '1.0-2' }} %
                            } @else {
                                —
                            }
                        </td>
                        <td [attr.data-label]="'Bestellung' | translate">
                            @if (item.taxInPercent?.orderValue !== null) {
                                {{ item.taxInPercent?.orderValue | number: '1.0-2' }} %
                            } @else {
                                —
                            }
                        </td>
                        <td [attr.data-label]="'Unterschied' | translate" class="fw-bold">
                            @if (taxRates(); as rates) {
                                {{ rates.invoice | number: '1.0-2' }} % {{ 'statt' | translate }}
                                {{ rates.order | number: '1.0-2' }} %
                            } @else {
                                —
                            }
                        </td>
                    </tr>
                }
                @if (item.articleNumberDeviates) {
                    <tr class="order-comparison-deviation">
                        <th scope="row">
                            <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            {{ 'Artikelnummer' | translate }}
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">
                            {{ item.supplierArticleNumber?.invoiceValue || '—' }}
                        </td>
                        <td [attr.data-label]="'Bestellung' | translate">
                            {{ item.supplierArticleNumber?.orderValue || '—' }}
                        </td>
                        <td [attr.data-label]="'Unterschied' | translate" class="fw-bold">
                            {{ 'andere Artikelnummer' | translate }}
                        </td>
                    </tr>
                }
                @if (item.unitDeviates) {
                    <tr class="order-comparison-deviation">
                        <th scope="row">
                            <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            {{ 'Einheit' | translate }}
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">{{ invoiceUnit() }}</td>
                        <td [attr.data-label]="'Bestellung' | translate">{{ orderUnit() }}</td>
                        <td [attr.data-label]="'Unterschied' | translate" class="fw-bold">
                            {{ 'andere Einheit' | translate }}
                        </td>
                    </tr>
                }
                @if (item.quantity; as quantity) {
                    <tr [class.order-comparison-deviation]="item.quantityDeviates">
                        <th scope="row">
                            @if (item.quantityDeviates) {
                                <invoice-item-deviation [hint]="'Weicht von der Bestellung ab' | translate" />
                            }
                            {{ 'Abrechnung' | translate }}
                        </th>
                        <td [attr.data-label]="'Rechnung' | translate">
                            {{ quantity.invoiceQuantity | number: '1.0-3' }} {{ invoiceUnit() }}
                        </td>
                        <td [attr.data-label]="'Bestellung' | translate">
                            @let orderedText = quantity.orderedQuantity | number: '1.0-3';
                            {{
                                '\{\{quantity\}\} \{\{unit\}\} bestellt'
                                    | translate: { quantity: orderedText, unit: orderedQuantityUnit() }
                            }}
                            · {{ 'insgesamt' | translate }}
                            {{ quantity.cumulativeInvoicedQuantity | number: '1.0-3' }}
                            {{ 'abgerechnet' | translate }}
                        </td>
                        <td [attr.data-label]="'Unterschied' | translate">
                            <invoice-item-quantity-status [quantity]="quantity" [unit]="orderedQuantityUnit()" />
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    }

    @if (item.linkedToOrder) {
        <footer class="order-comparison-footer">
            @if (item.matchingFields.length > 0) {
                <div class="text-success-emphasis">
                    <em class="fas fa-check me-1" aria-hidden="true"></em>
                    @if (deviationCount() === 0) {
                        {{ 'Geprüft und übereinstimmend' | translate }}:
                    } @else {
                        {{ 'Übereinstimmend' | translate }}:
                    }
                    @for (field of item.matchingFields; track field) {
                        <span>{{ field | translate }}{{ $last ? '' : ', ' }}</span>
                    }
                </div>
            }
            @if (discountPercentage() > 0 && item.price?.invoiceValue; as comparedPrice) {
                <div class="text-info-emphasis">
                    <em class="fas fa-circle-info me-1" aria-hidden="true"></em>
                    {{ 'Rechnung gewährt' | translate }} {{ discountPercentage() | number: '1.0-2' }} %
                    @if (item.currencyDeviates) {
                        {{ 'Rabatt' | translate }}
                    } @else if (discountedPriceIsAsOrdered()) {
                        {{ 'Rabatt, Preis vor Rabatt wie bestellt' | translate }}
                    } @else {
                        {{ 'Rabatt, verglichen wird der Preis vor Rabatt' | translate }}
                    }
                    (<display-currency
                        [value]="comparedPrice.amount"
                        [currencyCode]="comparedPrice.currency"
                    />)
                </div>
            }
            @if (showsGoodsReceiptHint()) {
                <div class="text-description">
                    <em class="fas fa-circle-info me-1" aria-hidden="true"></em>
                    {{ 'Kein Wareneingang zugeordnet' | translate }}
                </div>
            }
        </footer>
    }
</section>
